
import React, { useState } from 'react';
import { Building2, Users, ArrowRight, Mail, Phone, Fingerprint, Shield, Home, KeyRound, ChevronLeft, Globe } from 'lucide-react';
import Logo from './Logo';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../lib/supabase';
import { COUNTRIES_CURRENCIES } from '../constants';

interface LoginPageProps {
  onLogin: (role: 'business' | 'individual', identifier: string) => void;
  onNavigateToSignup: () => void;
  onBackToHome: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin, onNavigateToSignup, onBackToHome }) => {
  const [activeTab, setActiveTab] = useState<'business' | 'individual'>('business');
  const [view, setView] = useState<'login' | 'reset'>('login');
  const [resetMethod, setResetMethod] = useState<'email' | 'phone'>('email');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');
  const [isLoading, setIsLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const { t, language } = useLanguage();

  const selectedCountryData = COUNTRIES_CURRENCIES.find(c => c.country === 'Saudi Arabia') || COUNTRIES_CURRENCIES[0];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    if (identifier.toLowerCase() === 'admin@eventmanager.com') {
        onLogin(activeTab, identifier);
        setIsLoading(false);
        return;
    }

    try {
        const { data, error } = await supabase.auth.signInWithPassword({
            email: identifier, 
            password: password,
        });

        if (error) {
            alert(error.message);
        } else if (data.user) {
            onLogin(activeTab, identifier);
        }
    } catch (err) {
        console.error("Login error", err);
    } finally {
        setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (resetMethod === 'email') {
        const { error } = await supabase.auth.resetPasswordForEmail(identifier, {
          redirectTo: window.location.origin,
        });
        if (error) throw error;
      } else {
        // Construct full phone number with dial code if not already present
        const fullPhone = identifier.startsWith('+') ? identifier : `${selectedCountryData.dialCode}${identifier.replace(/^0+/, '')}`;
        const { error } = await supabase.auth.signInWithOtp({
            phone: fullPhone,
        });
        if (error) throw error;
      }
      setResetSent(true);
    } catch (err: any) {
      alert(err.message || "Failed to send reset code. Please check your connection.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdminShortcut = () => {
    setIdentifier('admin@eventmanager.com');
    setPassword('admin123');
    setLoginMethod('email');
    setView('login');
  };

  return (
    <div className="min-h-screen bg-wedding-50 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-wedding-200 rounded-full filter blur-3xl opacity-30 animate-pulse"></div>
        <div className="absolute -bottom-20 left-1/2 w-80 h-80 bg-pink-200 rounded-full filter blur-3xl opacity-30 animate-pulse delay-700"></div>
      </div>

      <div className="w-full max-w-md bg-white rounded-[2rem] shadow-2xl z-10 overflow-hidden border border-white/50 backdrop-blur-sm">
        <div className="p-8 pb-4 text-center">
          <div className="flex justify-between items-start mb-2">
            <button onClick={onBackToHome} className="text-gray-400 hover:text-wedding-600 transition-colors flex items-center text-[10px] font-black uppercase tracking-widest">
              <Home className="w-3.5 h-3.5 mr-1" />
              {t('button.backHome')}
            </button>
          </div>
          <div className="flex justify-center mb-6">
            <button 
              onClick={onBackToHome}
              className="w-16 h-16 bg-wedding-100 rounded-2xl flex items-center justify-center shadow-lg transform hover:scale-110 transition-transform cursor-pointer"
            >
              <Logo className="w-10 h-10 text-wedding-600" />
            </button>
          </div>
          <h1 className="text-3xl font-serif font-black tracking-tighter text-gray-900 mb-2 uppercase">
            {view === 'login' ? t('login.title') : 'Reset Password'}
          </h1>
          <p className="text-gray-400 text-[10px] font-black tracking-[0.2em] uppercase">
            {view === 'login' ? t('login.subtitle') : 'Recover your account access'}
          </p>
        </div>

        {view === 'login' ? (
          <>
            <div className="px-8 mb-4">
              <div className="flex bg-gray-50 p-1 rounded-2xl border border-gray-100">
                <button 
                  onClick={() => setActiveTab('business')} 
                  className={`flex-1 flex items-center justify-center py-3 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${activeTab === 'business' ? 'bg-white text-wedding-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <Building2 className="w-4 h-4 mr-2" />{t('login.business')}
                </button>
                <button 
                  onClick={() => setActiveTab('individual')} 
                  className={`flex-1 flex items-center justify-center py-3 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${activeTab === 'individual' ? 'bg-white text-wedding-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <Users className="w-4 h-4 mr-2" />{t('login.individual')}
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-8 pt-0 space-y-6">
              <div className="space-y-5">
                <div>
                  <div className="flex justify-between items-center mb-2 px-1">
                    <label className="block text-[10px] font-black uppercase tracking-widest text-gray-500">
                      {loginMethod === 'email' ? t('login.email') : t('login.phone')}
                    </label>
                    <button 
                      type="button" 
                      onClick={() => setLoginMethod(prev => prev === 'email' ? 'phone' : 'email')} 
                      className="text-[10px] font-black uppercase tracking-tighter text-wedding-600 hover:underline"
                    >
                      Use {loginMethod === 'email' ? 'Phone' : 'Email'}
                    </button>
                  </div>
                  <div className="relative group">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-wedding-500 transition-colors">
                      {loginMethod === 'email' ? <Mail className="w-5 h-5" /> : <Phone className="w-5 h-5" />}
                    </div>
                    <input
                      type={loginMethod === 'email' ? 'email' : 'tel'}
                      required
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:bg-white focus:ring-4 focus:ring-wedding-500/10 focus:border-wedding-500 outline-none text-sm transition-all shadow-inner"
                      placeholder={loginMethod === 'email' ? "manager@venue.com" : "+966 50 123 4567"}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2 px-1">
                    <label className="block text-[10px] font-black uppercase tracking-widest text-gray-500">{t('login.password')}</label>
                    <button 
                      type="button"
                      onClick={() => setView('reset')}
                      className="text-[10px] font-black uppercase tracking-tighter text-wedding-600 hover:underline"
                    >
                      Reset Password
                    </button>
                  </div>
                  <div className="relative group">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-wedding-500 transition-colors">
                      <Fingerprint className="w-5 h-5" />
                    </div>
                    <input 
                      type="password" 
                      required 
                      value={password} 
                      onChange={(e) => setPassword(e.target.value)} 
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:bg-white focus:ring-4 focus:ring-wedding-500/10 focus:border-wedding-500 outline-none text-sm transition-all shadow-inner" 
                      placeholder="••••••••" 
                    />
                  </div>
                </div>
              </div>

              <button 
                type="submit" 
                disabled={isLoading} 
                className="w-full bg-wedding-600 hover:bg-wedding-700 text-white font-black uppercase tracking-widest py-5 rounded-2xl shadow-xl shadow-wedding-200 transition-all flex items-center justify-center space-x-2 disabled:opacity-70 transform active:scale-95"
              >
                {isLoading ? (
                    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                    <>
                        <span>{activeTab === 'business' ? t('login.signin') : t('login.access')}</span>
                        <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                )}
              </button>

              <div className="text-center pt-2">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">{t('login.noAccount')} </span>
                <button 
                  type="button" 
                  onClick={onNavigateToSignup} 
                  className="text-xs font-black text-wedding-600 uppercase tracking-widest hover:underline ml-1"
                >
                  {t('button.signup')}
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="p-8 pt-0 animate-fadeIn">
            {resetSent ? (
              <div className="text-center py-10 space-y-6">
                <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto text-green-500 shadow-inner">
                  {resetMethod === 'email' ? <Mail className="w-10 h-10" /> : <Phone className="w-10 h-10" />}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Check your {resetMethod}</h3>
                  <p className="text-gray-500 text-sm">We've sent a recovery {resetMethod === 'email' ? 'link' : 'code'} to <strong>{identifier}</strong></p>
                </div>
                <button 
                  onClick={() => { setView('login'); setResetSent(false); }}
                  className="text-wedding-600 font-black uppercase tracking-widest text-xs hover:underline"
                >
                  Back to Login
                </button>
              </div>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-6">
                <div className="flex bg-gray-50 p-1 rounded-2xl border border-gray-100 mb-2">
                    <button 
                    type="button"
                    onClick={() => {setResetMethod('email'); setIdentifier('');}} 
                    className={`flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${resetMethod === 'email' ? 'bg-white text-wedding-600 shadow-sm' : 'text-gray-400'}`}
                    >
                    Email
                    </button>
                    <button 
                    type="button"
                    onClick={() => {setResetMethod('phone'); setIdentifier('');}} 
                    className={`flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${resetMethod === 'phone' ? 'bg-white text-wedding-600 shadow-sm' : 'text-gray-400'}`}
                    >
                    Phone
                    </button>
                </div>

                <p className="text-[10px] font-bold text-gray-400 text-center mb-6 uppercase tracking-widest">Enter your registered {resetMethod} to receive a reset {resetMethod === 'email' ? 'link' : 'code'}.</p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-gray-500 mb-2 px-1">{resetMethod === 'email' ? 'Email Address' : 'Mobile Number'}</label>
                    {resetMethod === 'email' ? (
                      <div className="relative group">
                          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-wedding-500 transition-colors">
                          <Mail className="w-5 h-5" />
                          </div>
                          <input
                          type="email"
                          required
                          value={identifier}
                          onChange={(e) => setIdentifier(e.target.value)}
                          className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:bg-white focus:ring-4 focus:ring-wedding-500/10 focus:border-wedding-500 outline-none text-sm transition-all shadow-inner"
                          placeholder="your@email.com"
                          />
                      </div>
                    ) : (
                      <div className="flex gap-3">
                          <div className="w-24 px-4 py-4 bg-gray-900 border border-gray-800 rounded-2xl text-[10px] font-black text-white flex items-center justify-center shadow-lg">
                          {selectedCountryData.dialCode}
                          </div>
                          <div className="flex-1 relative group">
                              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-wedding-500 transition-colors">
                                  <Phone className="w-5 h-5" />
                              </div>
                              <input
                                  type="tel"
                                  required
                                  value={identifier}
                                  onChange={(e) => setIdentifier(e.target.value)}
                                  className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:bg-white focus:ring-4 focus:ring-wedding-500/10 focus:border-wedding-500 outline-none text-sm font-bold tracking-widest"
                                  placeholder="50 123 4567"
                              />
                          </div>
                      </div>
                    )}
                  </div>
                </div>

                <button 
                  type="submit" 
                  disabled={isLoading || !identifier} 
                  className="w-full bg-gray-900 hover:bg-black text-white font-black uppercase tracking-widest py-5 rounded-2xl shadow-xl transition-all flex items-center justify-center space-x-2 disabled:opacity-70 transform active:scale-95"
                >
                  {isLoading ? (
                      <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                      <>
                          <span>Send Reset {resetMethod === 'email' ? 'Link' : 'Code'}</span>
                          <KeyRound className="w-5 h-5 ml-2" />
                      </>
                  )}
                </button>

                <div className="text-center">
                  <button 
                    type="button"
                    onClick={() => setView('login')}
                    className="flex items-center justify-center mx-auto text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-wedding-600 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Back to login
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>

      <button 
        onClick={handleAdminShortcut}
        className="mt-8 text-center text-gray-400 text-[10px] font-black uppercase tracking-widest opacity-50 hover:opacity-100 hover:text-wedding-600 transition-all flex items-center gap-2"
      >
        <Shield className="w-3 h-3" />
        Secure Admin Access Node • Global Encryption Enabled
      </button>
    </div>
  );
};

export default LoginPage;
